/**
 * Analytics Context Provider
 *
 * Provides Google Analytics 4 tracking throughout the application.
 * Automatically initializes GA based on site settings and tracks page views.
 */

import React, { createContext, useContext, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { useAuth } from './integrated-auth';
import {
  initializeGA,
  isAnalyticsReady,
  trackPageView,
  setUserId,
  setUserProperties,
  trackEvent,
  trackSignUp,
  trackLogin,
  trackLogout,
  trackLeagueCreate,
  trackLeagueJoin,
  trackLeagueLeave,
  trackLeagueView,
  trackSubmissionCreate,
  trackSubmissionView,
  trackVoteCast,
  trackVoteFinalize,
  trackMusicPlay,
  trackMusicServiceConnect,
  trackPlaylistCreate,
  trackCommentCreate,
  trackShareClick,
  trackInviteSend,
  trackSearch,
  trackError,
} from '@/services/analytics';

// Interface for public site settings that include GA config
interface PublicSettings {
  googleAnalyticsId: string | null;
  googleAnalyticsEnabled: boolean;
  googleAnalyticsDebugMode: boolean;
}

// Analytics context interface with all tracking methods
interface AnalyticsContextType {
  isReady: boolean;
  // Core tracking
  trackPageView: typeof trackPageView;
  trackEvent: typeof trackEvent;
  // Authentication
  trackSignUp: typeof trackSignUp;
  trackLogin: typeof trackLogin;
  trackLogout: typeof trackLogout;
  // Leagues
  trackLeagueCreate: typeof trackLeagueCreate;
  trackLeagueJoin: typeof trackLeagueJoin;
  trackLeagueLeave: typeof trackLeagueLeave;
  trackLeagueView: typeof trackLeagueView;
  // Submissions
  trackSubmissionCreate: typeof trackSubmissionCreate;
  trackSubmissionView: typeof trackSubmissionView;
  // Voting
  trackVoteCast: typeof trackVoteCast;
  trackVoteFinalize: typeof trackVoteFinalize;
  // Music
  trackMusicPlay: typeof trackMusicPlay;
  trackMusicServiceConnect: typeof trackMusicServiceConnect;
  trackPlaylistCreate: typeof trackPlaylistCreate;
  // Engagement
  trackCommentCreate: typeof trackCommentCreate;
  trackShareClick: typeof trackShareClick;
  trackInviteSend: typeof trackInviteSend;
  trackSearch: typeof trackSearch;
  // Errors
  trackError: typeof trackError;
}

// Create context with default no-op implementations
const AnalyticsContext = createContext<AnalyticsContextType>({
  isReady: false,
  trackPageView: () => {},
  trackEvent: () => {},
  trackSignUp: () => {},
  trackLogin: () => {},
  trackLogout: () => {},
  trackLeagueCreate: () => {},
  trackLeagueJoin: () => {},
  trackLeagueLeave: () => {},
  trackLeagueView: () => {},
  trackSubmissionCreate: () => {},
  trackSubmissionView: () => {},
  trackVoteCast: () => {},
  trackVoteFinalize: () => {},
  trackMusicPlay: () => {},
  trackMusicServiceConnect: () => {},
  trackPlaylistCreate: () => {},
  trackCommentCreate: () => {},
  trackShareClick: () => {},
  trackInviteSend: () => {},
  trackSearch: () => {},
  trackError: () => {},
});

/**
 * Custom hook to access analytics context
 */
export function useAnalytics(): AnalyticsContextType {
  return useContext(AnalyticsContext);
}

/**
 * Analytics Provider Component
 */
export function AnalyticsProvider({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();
  const { user, isAuthenticated } = useAuth();
  const previousLocation = useRef<string | null>(null);
  const previousUserId = useRef<string | null>(null);

  // Fetch public settings to get GA configuration
  const { data: settings } = useQuery<PublicSettings>({
    queryKey: ['/api/site-settings/public'],
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    refetchOnWindowFocus: false,
  });

  // Initialize GA when settings are loaded
  useEffect(() => {
    if (settings?.googleAnalyticsEnabled && settings?.googleAnalyticsId) {
      initializeGA(settings.googleAnalyticsId, settings.googleAnalyticsDebugMode);
    }
  }, [settings?.googleAnalyticsId, settings?.googleAnalyticsEnabled, settings?.googleAnalyticsDebugMode]);

  // Track page views on location change
  useEffect(() => {
    if (isAnalyticsReady() && location !== previousLocation.current) {
      // Get page title from document
      const title = document.title;
      trackPageView(location, title);
      previousLocation.current = location;
    }
  }, [location]);

  // Set user ID when authentication state changes
  useEffect(() => {
    const currentUserId = user?.id || null;

    if (isAnalyticsReady() && currentUserId !== previousUserId.current) {
      setUserId(currentUserId);

      // Set additional user properties if authenticated
      if (currentUserId && user) {
        setUserProperties({
          user_role: user.role || 'user',
          preferred_music_service: user.preferredMusicService || 'unknown',
        });
      }

      previousUserId.current = currentUserId;
    }
  }, [user, isAuthenticated]);

  // Create context value with all tracking methods
  const contextValue: AnalyticsContextType = {
    isReady: isAnalyticsReady(),
    trackPageView,
    trackEvent,
    trackSignUp,
    trackLogin,
    trackLogout,
    trackLeagueCreate,
    trackLeagueJoin,
    trackLeagueLeave,
    trackLeagueView,
    trackSubmissionCreate,
    trackSubmissionView,
    trackVoteCast,
    trackVoteFinalize,
    trackMusicPlay,
    trackMusicServiceConnect,
    trackPlaylistCreate,
    trackCommentCreate,
    trackShareClick,
    trackInviteSend,
    trackSearch,
    trackError,
  };

  return (
    <AnalyticsContext.Provider value={contextValue}>
      {children}
    </AnalyticsContext.Provider>
  );
}

export default AnalyticsProvider;
